import { cn, copyText } from "utils";

import { useState } from "react";
import { IconHover } from "../IconHover";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Chat/Action";
import { useCheckAccessClick } from "@/src/hooks/useCheckAccessClick";
import { useFeedback } from "@hooks/useFeedback";
import { buildOpenReplayLabel } from "@utils/openreplay";
import { stripDynamicUiEnvelopes } from "@features/etf-overlap/utils/stripDynamicUi";

const I18n = I18nUtil.createScoped(Translations);

const OR_SCENE = "ChatBox";

const Action = ({
  reload,
  markdown,
  messageId,
  onShare,
  rating,
  retry,
  isDeepSearchAgent,
  isTab,
  stripEnvelopeOnCopy,
  onRetryTracked,
}: {
  reload: () => void;
  onShare: () => void;
  markdown: string;
  messageId: string;
  rating: "thumb_up" | "thumb_down";
  retry: boolean;
  isDeepSearchAgent: boolean;
  isTab?: boolean;
  /** 仅重叠分析结果消息：复制时剥离 `<dynamic_ui>` 信封（EDG-561 RK-14）；其余消息逐字不变 */
  stripEnvelopeOnCopy?: boolean;
  /** 仅重叠分析计算失败消息传入（EDG-561 `overlap_result_retry`）：在原 `reload` 之前触发 */
  onRetryTracked?: () => void;
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const [like, setLike] = useState({
    isUpActive: rating === "thumb_up",
    isDownActive: rating === "thumb_down",
  });
  const { feedback } = useFeedback();

  const { handleAccessClick: handleShareClick } = useCheckAccessClick({
    originClick: () => {
      onShare();
    },
  });

  const { handleAccessClick: handleLikeUpClick } = useCheckAccessClick({
    originClick: () => {
      feedback(messageId, "thumb_up");
      setLike({
        isUpActive: true,
        isDownActive: false,
      });
    },
  });

  const { handleAccessClick: handleLikeDownClick } = useCheckAccessClick({
    originClick: () => {
      feedback(messageId, "thumb_down");
      setLike({
        isUpActive: false,
        isDownActive: true,
      });
    },
  });

  const handleReload = () => {
    onRetryTracked?.();
    reload();
  };

  const copy = () => {
    copyText(
      stripEnvelopeOnCopy ? stripDynamicUiEnvelopes(markdown) : markdown,
    );
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <div className="flex items-center py-3 cursor-pointer gap-6">
      {!isDeepSearchAgent && (
        <>
          <div className="flex gap-4">
            {isCopied ? (
              <IconHover
                title={I18n.t("Copied")}
                name="check"
                className="text-content_quaternary cursor-pointer"
                hoverClassName={isTab ? "left-[24px]" : ""}
              />
            ) : (
              <IconHover
                data-openreplay-label={buildOpenReplayLabel(
                  OR_SCENE,
                  "MessageActions",
                  "Copy",
                )}
                title={I18n.t("Copy")}
                name="copy"
                className="text-content_quaternary cursor-pointer"
                onClick={copy}
                hoverClassName={isTab ? "left-[22px]" : ""}
              />
            )}
            {retry && (
              <IconHover
                data-openreplay-label={buildOpenReplayLabel(
                  OR_SCENE,
                  "MessageActions",
                  "Regenerate",
                )}
                title={I18n.t("Regenerate")}
                // 图标钮没有文字，必须有无障碍名（EDG-561 SC-14）；视觉与埋点 label 不变
                aria-label={I18n.t("Regenerate")}
                name="revision"
                className="text-content_quaternary cursor-pointer"
                onClick={handleReload}
              />
            )}
            <IconHover
              data-openreplay-label={buildOpenReplayLabel(
                OR_SCENE,
                "MessageActions",
                "Share",
              )}
              title={I18n.t("Share")}
              onClick={handleShareClick}
              name="expand-alt"
              className="text-content_quaternary cursor-pointer"
            />
          </div>
          <div className="w-[1px] bg-background_white_overlay10 h-5" />
        </>
      )}

      <div className="flex gap-4">
        <IconHover
          data-openreplay-label={buildOpenReplayLabel(
            OR_SCENE,
            "MessageFeedback",
            "Like",
          )}
          title={I18n.t("Best reply")}
          name={like.isUpActive ? "thumbs-up-filled" : "thumbs-up"}
          className={cn(
            "text-content_quaternary cursor-pointer",
            like.isUpActive ? "text-content_brand_primary" : "",
          )}
          onClick={handleLikeUpClick}
        />
        <IconHover
          data-openreplay-label={buildOpenReplayLabel(
            OR_SCENE,
            "MessageFeedback",
            "Dislike",
          )}
          title={I18n.t("Dislike")}
          name={like.isDownActive ? "thumbs-down-filled" : "thumbs-down"}
          className={cn(
            "text-content_quaternary cursor-pointer",
            like.isDownActive ? "text-content_brand_primary" : "",
          )}
          onClick={handleLikeDownClick}
        />
      </div>
    </div>
  );
};

/** A question left without an answer (failed, or stopped before any output) only offers Regenerate. */
export const UnansweredTurnAction = ({ reload }: { reload: () => void }) => (
  <div className="flex items-center py-3">
    <IconHover
      data-openreplay-label={buildOpenReplayLabel(
        OR_SCENE,
        "UnansweredTurn",
        "Regenerate",
      )}
      title={I18n.t("Regenerate")}
      aria-label={I18n.t("Regenerate")}
      name="revision"
      className="text-content_quaternary cursor-pointer"
      onClick={reload}
    />
  </div>
);

export default Action;
